/**
 * The interpreter's wrap-up: the breather between one call and the next.
 *
 * The server never puts an interpreter back in the queue by itself. Every call
 * end leaves them `online` — connected but not offerable — until a client
 * sends `interpreter_status: available` (resetParticipantStatuses in
 * vrs-server/ws/call-lifecycle.js; pinned in vrs-server/__tests__/ws-queue-flow.test.js).
 * So "Pause after this call" needs no server flag: the client simply does not
 * re-announce. What this module decides is WHEN it re-announces:
 *
 * - breather: back in the queue 15 s after the call (the brand's
 *   `operations.wrapUpBreatherMs`), sooner when a client they could take is
 *   waiting (3 s after the call, `operations.wrapUpClientWaitingMs`, once the
 *   card has said so) or when the interpreter taps the card's first button,
 *   unless they choose to pause instead;
 * - paused:   not back until they choose Available (asked for in the call,
 *   chosen on the card, or they were not taking queue calls before it);
 * - break:    "Take 5", a server-recorded break that returns them to
 *   Available when it runs out.
 *
 * Pure helpers only; the timers and requests live in wrapUpActions.ts. The web
 * dashboard (interpreter-profile.html) reads the same record the conference
 * page writes here and runs its own copy of the timing (wrapUpTiming there
 * follows the same rules as wrapUpTiming here).
 */
/** Default breather; a tenant sets its own with `operations.wrapUpBreatherMs`. */
export const WRAP_UP_BREATHER_MS = 15000;
/**
 * Default breather, from the call's end, while a client is waiting; a tenant
 * sets its own with `operations.wrapUpClientWaitingMs`.
 */
export const WRAP_UP_CLIENT_WAITING_MS = 3000;
/** Longest breather a tenant setting may ask for. */
export const WRAP_UP_SETTING_MAX_MS = 10 * 60 * 1000;
/**
 * Least time the card shows "A client is waiting" before it returns the
 * interpreter by itself, so they see what is about to happen and can still
 * choose Stay paused or Take 5. Never past the full breather.
 */
export const WRAP_UP_CLIENT_WAITING_NOTICE_MS = 1500;
export const TAKE_FIVE_MS = 5 * 60 * 1000;
/** Shown on the card while the queue reports a waiting client. */
export const WRAP_UP_CLIENT_WAITING_TEXT = 'A client is waiting';
/**
 * The tenant's breather lengths from its whitelabel `operations`. A missing
 * or invalid value (not a number, negative, over 10 minutes) keeps the default.
 */
export function wrapUpTiming(operations) {
    const settings = typeof operations === 'object' && operations !== null
        ? operations : {};
    const read = (key, fallback) => {
        const value = settings[key];
        return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= WRAP_UP_SETTING_MAX_MS
            ? Math.round(value) : fallback;
    };
    const breatherMs = read('wrapUpBreatherMs', WRAP_UP_BREATHER_MS);
    return {
        breatherMs,
        clientWaitingMs: Math.min(breatherMs, read('wrapUpClientWaitingMs', WRAP_UP_CLIENT_WAITING_MS))
    };
}
/**
 * When a breather ends: the full breather; while a client is waiting,
 * `clientWaitingMs` after the call but not before the card has said so for
 * WRAP_UP_CLIENT_WAITING_NOTICE_MS (and never past the full breather).
 */
export function breatherReturnAt(wrapUp, timing) {
    const full = wrapUp.endedAt + timing.breatherMs;
    if (!wrapUp.clientWaiting) {
        return full;
    }
    const noticed = (wrapUp.clientWaitingSince ?? wrapUp.endedAt) + WRAP_UP_CLIENT_WAITING_NOTICE_MS;
    return Math.min(full, Math.max(wrapUp.endedAt + timing.clientWaitingMs, noticed));
}
/**
 * The breather card's primary button. It says what a tap does: with a client
 * waiting it takes that call; otherwise it only makes the interpreter Available.
 */
export function takeNowLabel(wrapUp) {
    return wrapUp.clientWaiting ? 'Take the call now' : 'Go available now';
}
/** Web only: sessionStorage record handed from the conference page to the dashboard. */
export const WRAP_UP_STORAGE_KEY = 'vrs_interpreter_wrap_up';
export const WRAP_UP_RECORD_VERSION = 1;
/** Seconds from a stored call start (native epoch ms, web ISO string) to its end. */
export function callDurationSeconds(startedAt, endedAt) {
    const started = typeof startedAt === 'number'
        ? startedAt
        : typeof startedAt === 'string' ? Date.parse(startedAt) : Number.NaN;
    if (!Number.isFinite(started) || started > endedAt) {
        return null;
    }
    return Math.round((endedAt - started) / 1000);
}
export function ordinal(value) {
    const n = Math.max(0, Math.floor(value));
    const lastTwo = n % 100;
    const suffixes = { 1: 'st', 2: 'nd', 3: 'rd' };
    const suffix = lastTwo >= 11 && lastTwo <= 13 ? 'th' : suffixes[n % 10] || 'th';
    return `${n}${suffix}`;
}
/** "Call complete · 14 min · 7th today" — whatever parts are known. */
export function wrapUpSummary(wrapUp) {
    const parts = ['Call complete'];
    if (typeof wrapUp.durationSeconds === 'number') {
        parts.push(wrapUp.durationSeconds < 60 ? 'under 1 min' : `${Math.round(wrapUp.durationSeconds / 60)} min`);
    }
    if (typeof wrapUp.todayCalls === 'number' && wrapUp.todayCalls > 0) {
        parts.push(`${ordinal(wrapUp.todayCalls)} today`);
    }
    return parts.join(' · ');
}
function clock(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
/** The one status line under the summary. */
export function wrapUpStatus(wrapUp, now) {
    if (wrapUp.phase === 'paused') {
        return 'Paused';
    }
    const seconds = Math.max(0, Math.ceil(((wrapUp.returnAt ?? now) - now) / 1000));
    if (wrapUp.phase === 'break') {
        return seconds > 0 ? `On break · back in ${clock(seconds)}` : 'Ending your break…';
    }
    return seconds > 0 ? `Back in queue in ${seconds} s` : 'Going back in queue…';
}
/**
 * The wrap-up a finished call starts. Only an interpreter who was taking queue
 * calls before it gets the breather; anyone else stays where they were. A
 * client already waiting for them when the call ends shortens the breather
 * from the start.
 */
export function startWrapUp({ activeCall, clientWaiting = false, now, pauseAfterCall, timing, userId, wasAvailable }) {
    const breather = wasAvailable && !pauseAfterCall;
    const waiting = breather && clientWaiting;
    return {
        callId: typeof activeCall?.callId === 'string' && activeCall.callId ? activeCall.callId : undefined,
        ...waiting ? { clientWaiting: true, clientWaitingSince: now } : {},
        durationSeconds: callDurationSeconds(activeCall?.startedAt, now),
        endedAt: now,
        phase: breather ? 'breather' : 'paused',
        returnAt: breather
            ? breatherReturnAt({ clientWaiting: waiting, endedAt: now }, timing || wrapUpTiming(undefined))
            : undefined,
        userId
    };
}
export function toWrapUpRecord(wrapUp, pauseAfterCall) {
    return {
        callId: wrapUp.callId,
        durationSeconds: wrapUp.durationSeconds,
        endedAt: wrapUp.endedAt,
        pauseAfterCall,
        userId: wrapUp.userId,
        version: WRAP_UP_RECORD_VERSION
    };
}
/**
 * Hand the finished call to the dashboard the browser is about to load. The
 * record lives in this tab's sessionStorage only: another tab, or a later
 * browser session, must never inherit a wrap-up (or a pause) it did not see.
 */
export function writeBrowserWrapUpRecord(record) {
    try {
        const storage = typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
        if (!storage) {
            return false;
        }
        storage.setItem(WRAP_UP_STORAGE_KEY, JSON.stringify(record));
        return true;
    }
    catch {
        return false;
    }
}
